import { Edge, MarkerType } from '@xyflow/react';

/**
 * A single user-defined property attached to an edge (relationship).
 * Mirrors the property model used by ConceptNode so the UI feels consistent.
 */
export interface EdgeProperty {
  name: string;
  type?: string; // 'str' | 'int' | 'float' | 'bool' | 'datetime' | 'list' | 'dict'
  value?: string;
}

/**
 * Canonical relationship type. Kept loose (string) so users can also enter
 * a free-form value. The dropdown shows the canonical list below.
 */
export type RelationshipType =
  | 'is-a'
  | 'part-of'
  | 'has-property'
  | 'relates-to'
  | 'depends-on'
  | 'implements'
  | 'extends'
  | 'uses'
  | 'contains'
  | 'produces'
  | 'consumes'
  | 'requires'
  | 'includes'
  | 'applies-to'
  | 'custom';

export const RELATIONSHIP_TYPE_OPTIONS: Array<{ value: RelationshipType; label: string }> = [
  { value: 'relates-to', label: 'relates to' },
  { value: 'is-a', label: 'is a' },
  { value: 'part-of', label: 'part of' },
  { value: 'has-property', label: 'has property' },
  { value: 'depends-on', label: 'depends on' },
  { value: 'implements', label: 'implements' },
  { value: 'extends', label: 'extends' },
  { value: 'uses', label: 'uses' },
  { value: 'contains', label: 'contains' },
  { value: 'produces', label: 'produces' },
  { value: 'consumes', label: 'consumes' },
  { value: 'requires', label: 'requires' },
  { value: 'includes', label: 'includes' },
  { value: 'applies-to', label: 'applies to' },
  { value: 'custom', label: 'custom' },
];

/**
 * Data payload carried by every ontology edge. The visible `edge.label`
 * stays in sync with `data.label` (we duplicate it on the edge so React
 * Flow's default rendering still works if a custom edge is not used).
 */
export interface ConceptEdgeData extends Record<string, unknown> {
  label: string;
  relationshipType?: RelationshipType | string;
  description?: string;
  properties: EdgeProperty[];
  bidirectional?: boolean;
  weight?: number;
  /** Who authored/last-edited this relationship (A9/A10): 'ai' or 'user'. */
  origin?: 'ai' | 'user';
  /** Provenance — 'ai' (default), 'user', 'corrected'. Persisted as first-class
   *  columns on the edge; lower-priority than nodes in the UI (no badge). */
  provenance?: 'ai' | 'user' | 'corrected';
  /** Display name / email of the original creator (null = AI-generated). */
  createdByName?: string | null;
  /** Display name / email of the last human editor. */
  updatedByName?: string | null;
  /** ISO timestamp of the first human correction (set once). */
  correctedAt?: string | null;
  /** The AI's short reason for relating the two concepts (A10), if generated. */
  rationale?: string;
  /**
   * True while this edge has been edited locally but the ontology has not yet
   * been saved to the backend. Cleared by handleSave on a successful round-trip.
   * Drives the "unsaved changes" visual indicator on the canvas edge.
   */
  unsaved?: boolean;
}

/**
 * Default edge data for a freshly-created edge (drag-to-link, AI link, etc.).
 */
export const createDefaultEdgeData = (
  overrides: Partial<ConceptEdgeData> = {},
): ConceptEdgeData => ({
  label: 'relates to',
  relationshipType: 'relates-to',
  description: '',
  properties: [],
  bidirectional: false,
  ...overrides,
});

/**
 * Build a ReactFlow Edge object with the project-wide visual style and
 * a populated `data` payload. Centralised so creation paths (drag, AI
 * suggestion, modal) all stay in sync.
 */
export const buildConceptEdge = (params: {
  id: string;
  source: string;
  target: string;
  edgeColor: string;
  data?: Partial<ConceptEdgeData>;
}): Edge => {
  const data = createDefaultEdgeData(params.data);
  const marker = { type: MarkerType.ArrowClosed, color: params.edgeColor };
  return {
    id: params.id,
    source: params.source,
    target: params.target,
    type: 'floating',
    animated: false,
    style: { stroke: params.edgeColor, strokeWidth: 2.5 },
    markerEnd: marker,
    ...(data.bidirectional ? { markerStart: marker } : {}),
    label: data.label,
    data,
  };
};

/**
 * Apply updated edge data back onto an Edge, keeping label/markers in sync.
 */
export const applyEdgeDataUpdate = (
  edge: Edge,
  patch: Partial<ConceptEdgeData>,
  edgeColor: string,
): Edge => {
  const prev = (edge.data ?? {}) as ConceptEdgeData;
  const nextData: ConceptEdgeData = {
    ...createDefaultEdgeData(prev),
    ...prev,
    ...patch,
  };
  const marker = { type: MarkerType.ArrowClosed, color: edgeColor };
  return {
    ...edge,
    label: nextData.label,
    data: nextData,
    markerEnd: marker,
    markerStart: nextData.bidirectional ? marker : undefined,
    style: {
      ...(edge.style || {}),
      stroke: edgeColor,
      strokeWidth: 2.5,
    },
  };
};

// Made with Bob
